import type { TicketField, WorkOrderCategory, WorkOrderPriority, WorkOrderSource } from './types';

export type ReportDays = 30 | 90 | 365;

export interface DurationStats {
  average: number | null;
  median: number | null;
}

export interface SatisfactionStats {
  responses: number;
  average: number | null;
  /** Percent of ratings that were 4 or 5. */
  satisfiedRate: number | null;
}

export interface OverviewReport {
  days: ReportDays;
  timezone: string;
  totals: {
    open: number;
    unassigned: number;
    awaitingFirstResponse: number;
    created: number;
    resolved: number;
    reopened: number;
    resolutionHours: DurationStats;
    firstResponseHours: DurationStats;
    satisfaction: SatisfactionStats;
  };
  volumeByBuilding: { buildingId: string | null; name: string; created: number; open: number; resolved: number; resolutionHours: DurationStats }[];
  resolutionTrend: ({ key: string; label: string; created: number; resolved: number } & DurationStats)[];
  workload: {
    userId: string | null;
    name: string;
    open: number;
    byPriority: Record<WorkOrderPriority, number>;
    oldestOpenDays: number;
    awaitingFirstResponse: number;
  }[];
  openByDepartment: { departmentId: string | null; path: string; open: number; byPriority: Record<WorkOrderPriority, number> }[];
  openByPriority: { priority: WorkOrderPriority; open: number }[];
}

interface PerformanceStats {
  resolutionHours: DurationStats;
  firstResponseHours: DurationStats;
  firstTouchResolved: number;
  firstTouchEligible: number;
  firstTouchRate: number | null;
  minutesLogged: number;
  satisfaction: SatisfactionStats;
}

export interface TechnicianRow extends PerformanceStats {
  userId: string;
  name: string;
  resolved: number;
  firstResponses: number;
  openNow: number;
}

export interface TechnicianReport {
  days: ReportDays;
  /** Totals for the whole desk; only administrators get them. */
  team: (PerformanceStats & { resolved: number }) | null;
  technicians: TechnicianRow[];
}

export interface SatisfactionReport {
  days: ReportDays;
  surveyDepartments: string[];
  totals: { sent: number; responseRate: number | null } & SatisfactionStats;
  distribution: { rating: number; count: number }[];
  trend: ({ key: string; label: string } & SatisfactionStats)[];
  byDepartment: ({ departmentId: string | null; path: string; sent: number; responseRate: number | null } & SatisfactionStats)[];
  byTechnician: ({ userId: string; name: string; sent: number; responseRate: number | null } & SatisfactionStats)[];
  responses: {
    workOrder: { id: string; number: number; title: string };
    rating: number;
    comment: string | null;
    respondedAt: string;
    department: string;
    technician: string | null;
  }[];
}

export interface TimeGroup {
  key: string;
  label: string;
  minutes: number;
  tickets: number;
  cost: number | null;
}

export interface TimeReport {
  days: ReportDays;
  hourlyRate: number | null;
  /** Technicians only see their own time. */
  onlyMine: boolean;
  totals: { minutes: number; tickets: number; entries: number; cost: number | null };
  byTechnician: TimeGroup[];
  byTeam: TimeGroup[];
  byBuilding: TimeGroup[];
  trend: { key: string; label: string; minutes: number }[];
}

export interface VolumeReport {
  days: ReportDays;
  total: number;
  bySource: { source: WorkOrderSource; count: number }[];
  sourceTrend: ({ key: string; label: string } & Record<WorkOrderSource, number>)[];
  byCategory: { category: WorkOrderCategory; count: number }[];
  fields: TicketField[];
  byField: { field: TicketField; rows: { value: string; label: string; count: number; open: number }[]; notFilledIn: number } | null;
}

export type JobKind = 'SYNC' | 'SMART_RULE' | 'RECURRING' | 'WORKFLOW';

export interface AutomationJob {
  kind: JobKind;
  id: string;
  name: string;
  enabled: boolean;
  schedule: string;
  lastRunAt: string | null;
  lastStatus: 'SUCCEEDED' | 'SKIPPED' | 'FAILED' | null;
  lastMessage: string | null;
  nextRunAt: string | null;
  overdue: boolean;
  runs: number;
  failures: number;
  days: { date: string; succeeded: number; skipped: number; failed: number }[];
}

export interface AutomationStatusReport {
  timezone: string;
  days: string[];
  jobs: AutomationJob[];
  summary: { jobs: number; enabled: number; failing: number; overdue: number };
  queue: { queuedEvents: number; oldestQueuedAt: string | null; failedEvents: number; waitingRuns: number };
}

export const RATING_LABEL: Record<number, string> = {
  1: 'Very unsatisfied',
  2: 'Unsatisfied',
  3: 'Okay',
  4: 'Satisfied',
  5: 'Very satisfied',
};

export const REPORT_SOURCE_LABEL: Record<WorkOrderSource, string> = {
  REQUESTER: 'Portal',
  EMAIL: 'Email',
  STAFF: 'Logged by the team',
  SMART_RULE: 'Smart rules',
  RECURRING: 'Scheduled tickets',
  PREVENTIVE: 'Preventive maintenance',
  INSPECTION: 'Inspection follow-ups',
};

export const JOB_KIND_LABEL: Record<JobKind, string> = {
  SYNC: 'Integration syncs',
  SMART_RULE: 'Smart rules',
  RECURRING: 'Scheduled tickets',
  WORKFLOW: 'Time-based workflows',
};

export const TICKET_FIELD_TYPE_LABEL: Record<TicketField['type'], string> = {
  SELECT: 'Choose from a list',
  TEXT: 'Text',
  NUMBER: 'Number',
  CHECKBOX: 'Yes or no',
};

/** "45m", "6.5h", "3.2d" */
export function formatHours(hours: number | null | undefined) {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${Math.round(hours * 10) / 10}h`;
  return `${Math.round((hours / 24) * 10) / 10}d`;
}

export const formatPercent = (value: number | null | undefined) => (value === null || value === undefined ? '—' : `${Math.round(value)}%`);

export const formatRating = (value: number | null | undefined) => (value === null || value === undefined ? '—' : `${value.toFixed(1)} / 5`);

export const formatHoursLogged = (minutes: number) => `${(Math.round((minutes / 60) * 10) / 10).toLocaleString('en-US')}h`;

/** Days in the chosen period, e.g. "the last 90 days". */
export const periodLabel = (days: ReportDays) => `the last ${days} days`;
